import { Grid, Button, TableRow, TableBody, TableCell, Table, TableContainer } from '@material-ui/core';
import { FormControl, InputLabel, Select, MenuItem, Typography, TextField } from '@material-ui/core';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import * as React from 'react';
import { useContext, useState } from 'react';
import { AuthContext } from '../_auth/AuthContext';
import { GetPlansWithCodeCount } from '../_service/plan';
import { ServicePlan } from '../_types/serviceplan';
import { ISearchResult } from '../_types/search';
import { PlanByLocationSelector } from '../common/Selector/PlanByLocationSelector';
import { CountCodesByDateAPI, DeleteCodesByDateAPI } from '../_service/bulk';
import { ILocationSummary } from '../_types/user';
import { BulkDeleteCodeTableRows } from '../_types/bulk';
import { BulkCodeConfirmDialog } from '../common/BulkCodeConfirmDialog';
import { BulkShowResultsListButton } from '../common/BulkShowResultsListButton';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    formControl: {
      margin: theme.spacing(1),
      minWidth: 120,
    },
    inputLabel: {
      fontSize: 14
    },
    warningLabel: {
      fontSize: 14,
      color: "red"
    },
    table: {
      width: "100%"
    },
    rows: {
        opacity:"1",
        transition : "all .3s",
        WebkitTransition: "all .3s",
        MozTransition: "all .3s"   
    }
  })
);

const DeleteCodes: React.FC = () => {
  const { user } = useContext(AuthContext);
  const [plans, setPlans] = useState<ServicePlan[]>([]);
  const [fromPlan, setFromPlan] = useState<string | null>();
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [isCounting, setIsCounting] = useState<boolean>(false);
  const [deletedCodeNumber, setDeletedCodeNumber] = useState<number>(0);
  const [storedDeletedCodeNumber, setStoredDeletedCodeNumber] = useState<number>(0);
  const classes = useStyles();
  const [deleteAlert, setDeleteAlert] = useState<Date | null>(null);
  const [storedDeleteDate, setStoredDeleteDate] = useState<Date | null>(null);
  const [deleteAmount, setDeleteAmount] = useState<number>(0);
  const [codeType, setCodeType] = useState<string>("Select Code Type");
  const [fromLocation, setFromLocation] = useState<ILocationSummary | ISearchResult | null>(user.currentLocation || null);
  const [codesInPeriod, setCodesInPeriod] = useState<BulkDeleteCodeTableRows[] | null>(null);
  let errors: JSX.Element[] = [];


  //calculates codes in a given date range.
  const calculateAndCountCodes = async () => {
    if (typeof (fromPlan) === 'number' && codeType !== "Select Code Type") {
      setIsCounting(true);
      let response: BulkDeleteCodeTableRows[] | null = await CountCodesByDateAPI(fromPlan, codeType);
      if (response === null) {
        setIsCounting(false);
        return alert('Something unexpected went wrong.');
      } else {
        setIsCounting(false);
        let reverseResponse = response.reverse();
        setCodesInPeriod(reverseResponse);
      }
    } else {
      setCodesInPeriod(null);
    }
  }

  const checkErrors = async () => {
    if (codeType === "Select Code Type") {
      errors.push(<li>Please select a code type to move</li>);
    }
    if (!fromPlan || fromPlan === "") {
      errors.push(<li>Must select a plan to delete codes from</li>);
    }

    return errors;
  }

  const filterPlans = (plans: ServicePlan[]): ServicePlan[] => {
    let filteredResults: ServicePlan[] = [];
    if (codeType === "otuc") {
      for (let plansFilter = 0; plansFilter < plans.length; plansFilter++) {
        if (plans[plansFilter].has_use_code === 1) {
          filteredResults.push(plans[plansFilter])
        }
      }
    } else if (codeType === "drb") {
      for (let plansFilter = 0; plansFilter < plans.length; plansFilter++) {
        if (plans[plansFilter].has_drb_code === 1) {
          filteredResults.push(plans[plansFilter])
        }
      }
    } else {
      return plans;
    }
    return filteredResults;
  }

  const _deleteByDate = async () => {
    if (codeType === "Select Code Type") return alert('Please select a code type to move');
    if (!fromPlan || fromPlan === "") return alert('Must select a plan to move from');
    if (deleteAlert === null) return alert('Something is wrong, no date set')
    if (user.id === undefined) return alert ('Something is wrong, please log in')
    let response: number | null;
    setIsDeleting(true);
      response = await DeleteCodesByDateAPI(fromPlan, deleteAlert, codeType, user.id);
    if (response === null) {
      setIsDeleting(false);
      return alert('Something unexpected went wrong. Check if codes were moved.');
    }
    if (response !== null) {
      if (user.currentLocation) {
        setFromLocation(user.currentLocation);
        GetPlansWithCodeCount(user.currentLocation.id).then(res => {
          setPlans(res);
        });
      } else if (fromLocation !== null || fromLocation!== undefined) {
        GetPlansWithCodeCount(fromLocation?.id).then(res => {
          setPlans(res);
        });
    
      }
      if (response !== 0) {
        setDeletedCodeNumber(response);      
        setIsDeleting(false);
      }
      if (response === 0) {
        return alert('No codes were deleted. Make sure you selected the correct plans.')
      }
    }
  }

  React.useEffect(() => {
    setFromPlan(null);
    setPlans([]);
    if (user.currentLocation) {
      GetPlansWithCodeCount(user.currentLocation.id).then(res => {
        setPlans(res);
      });
    }
  }, [user.currentLocation]);

  // when locations are changed, we need to repopulate the dropdown with plans from that location, 
  // remove the current plan selected since that is no longer applicable and remove the table if it is there
  React.useEffect(() => {
    GetPlansWithCodeCount(fromLocation?.id).then(res => {
      setPlans(res);
    });
    setFromPlan(null);
    setCodesInPeriod(null);
  }, [fromLocation]);

  // when plans are changed, repopulate the table
  React.useEffect(() => {
    calculateAndCountCodes();
  }, [fromPlan]);

  // when codetype is changed, the plan and table need to be blanked out.  This way, the admin uses the dropdown to see the new correct information.
  React.useEffect(() => {
    setFromPlan(null);
    setCodesInPeriod(null);
  }, [codeType]);

  let showTable = <></>;
  if (codesInPeriod !== null) {
    showTable = (
      <>
        <Grid container style={{ margin: "10px" }}>
          <Grid item xs={12} sm={12}>
            <TableContainer style={{ maxHeight: 350 }}>
              <Table stickyHeader className={classes.table} aria-label="simple table">
                <TableBody >
                  <TableRow>
                    <TableCell align="center">Date</TableCell>
                    <TableCell align="center">Number of Codes</TableCell>
                    <TableCell align="center">Delete?</TableCell>
                  </TableRow>

                  {(codesInPeriod as BulkDeleteCodeTableRows[]).map(n => {
                    return (
                      <>
                        <TableRow id={(n.date).toString()} className={classes.rows}>
                          <TableCell align="center">{new Date(n.date).toLocaleDateString(undefined, { // you can use undefined as first argument
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                          })
                          }</TableCell>
                          <TableCell align="center">{n.numOfCodes}</TableCell>
                          <TableCell align="center">
                            <Button variant="outlined"
                              color="primary"
                              onClick={() => {
                                setDeleteAlert(n.date);
                                setDeleteAmount(n.numOfCodes);
                              }}>Delete</Button>
                          </TableCell>
                        </TableRow>
                      </>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </>
    );

  }
  let alertAppear = <></>;

  if (deletedCodeNumber>0) {
    alertAppear = <BulkCodeConfirmDialog
      title={"Finished"}
      isOpen={true}
      contents={"Successfully deleted"}
      handleClose={() => {
        setStoredDeletedCodeNumber(deletedCodeNumber);
        setDeletedCodeNumber(0);
        let deletedDate = deleteAlert;
        setStoredDeleteDate(deletedDate);
        setDeleteAlert(null);
        
        if (deletedDate === null) return alert('Something went wrong')
        var obj = document.getElementById((deletedDate.toString()));
        if (obj !== undefined && obj!== null && obj.style!==null) {
          obj.style.opacity = '0';
          window.setTimeout(
            function removethis()
            {
              if (obj !== undefined && obj!== null && obj.style!==null) {
              obj.style.display='none';
              }
            }, 600);
        }
      }}
    />

  } else if (isDeleting) {
    alertAppear = <BulkCodeConfirmDialog
    title={"Deleting"}
    isOpen={true}
    contents={"Please wait"}
    />
  } else if ((typeof(deleteAlert) === 'string')) {
    alertAppear = <BulkCodeConfirmDialog
    title={"Are you sure you wish to delete codes on " + (new Date((deleteAlert))).toLocaleDateString() + "?"}
    isOpen={true}
    contents={"This will delete " + deleteAmount + (deleteAmount===1 ? " code" : " codes") + " total"}
    handleAction={()=> {
      _deleteByDate();
    }}
    handleClose={() => {
      setDeleteAlert(null);
      setDeletedCodeNumber(0);
    }} />
  }

  checkErrors();
  let filteredPlans = filterPlans(plans);
  return (
    <>
      <Grid item>
        <h3>Delete Codes from a Plan</h3>
        <p>First, select a location and a plan whose codes you want to delete. Second, select a location and a plan you want to delete codes from.  This will bring up a table listing dates and the number of codes created on that date.  A button to the right will allow you to delete those codes </p>
      </Grid>

      <Grid item xs={12} sm={5}>
        <FormControl className={classes.formControl}>
          <InputLabel id="select-label">{"Select Code Type"}</InputLabel>
          <Select
            labelId="select-label"
            id="select-outlined"
            label={"Select Code"}
            value={codeType}
            onChange={(e) => { setCodeType(String(e.target.value)) }}>
            <MenuItem value={'Select Code Type'}>Select Code Type</MenuItem>
            <MenuItem value={'drb'}>DRB Codes</MenuItem>
            <MenuItem value={'otuc'}>One Time Use Codes (OTUC) </MenuItem>
          </Select>

        </FormControl>
      </Grid>

      {/* LOCATION */}
      <PlanByLocationSelector
        onLocationChange={(newValue: ISearchResult | null) => {
          setFromLocation(newValue);
        }}
        planInputLabel='From'
        onPlanChange={(event: React.ChangeEvent<{ name?: string | undefined; value: unknown; }>) => setFromPlan(event.target.value as string)}
        planValue={fromPlan || ''}
        plans={filteredPlans ?? null}
        codeTypeCheck={codeType}
      />

      {showTable}
      {alertAppear}

      <Grid container style={{ margin: "10px" }}>
        <Grid item sm={6} xs={6} >
          <Typography className={classes.warningLabel}
            title="Errors that need to be handled">
            <ul>
              {errors}
            </ul>
          </Typography>
          <Typography
            title="Building Table... Please wait" className={classes.inputLabel}>
            {isCounting ?
              <ul>
                Building Table... please wait.
              </ul> :
              <ul>
              </ul>}
          </Typography>
        </Grid>
      </Grid>
      <Grid item xs={12} sm={12} style={{ display: "flex", justifyContent: "flex-start", flexDirection:"column"}}>
        <BulkShowResultsListButton
          hide={storedDeletedCodeNumber === 0}
          hideButton={true}
          title={"Successfully Deleted " + storedDeletedCodeNumber + (storedDeletedCodeNumber === 1 ? " Code created on " : " Codes created on ") + (storedDeleteDate ? (new Date((storedDeleteDate))).toLocaleDateString() : "Unknown Date")}
        />
      </Grid>
    </>
  );
}

export default DeleteCodes;